import React from 'react';

import { Box, MenuItem, TextField } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import useGetStatusChangeReasons from '@hooks/queries/useGetStatusChangeReasons';

interface AccountStatusFormReason {
    control: Control;
}

const AccountStatusFormReason = ({ control }: AccountStatusFormReason) => {
    const { data: reasons, isLoading } = useGetStatusChangeReasons();

    /**
     * Note: 'Reasons' is something we are migrating away from as an organisation. Instead, 'Status' should be used.
     * However, for legacy customer reasons we will show reasons if they do exist
     * Form can be submitted without a reason so no need to handle missing reasons
     */

    return (
        <Box sx={{ mt: 4.25 }}>
            <Box>
                {!isLoading && !!reasons?.length && (
                    <Controller
                        control={control}
                        name="reason"
                        render={({ field: { onChange, value } }) => (
                            <TextField
                                id="reason-dropdown"
                                data-testid="reason-dropdown"
                                select
                                onChange={onChange}
                                value={value || ''}
                                fullWidth
                                label={
                                    <FormattedMessage
                                        id="AccountStatusForm.reasonSelection"
                                        defaultMessage="Reason"
                                    />
                                }>
                                {reasons?.map((givenReason) => (
                                    <MenuItem
                                        key={`reason-${givenReason.reason_id}`}
                                        value={givenReason.reason_id}>
                                        {givenReason.description}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                )}
            </Box>
            <Box sx={{ mt: 3.75 }}>
                <Controller
                    control={control}
                    name="description"
                    render={({
                        field: { onChange, value },
                        fieldState: { error },
                    }) => (
                        <TextField
                            sx={{ mb: 0 }}
                            helperText={error ? error.message : null}
                            error={!!error}
                            onChange={onChange}
                            value={value}
                            multiline
                            rows={4}
                            fullWidth
                            label={
                                <FormattedMessage
                                    id="AccountStatusForm.reasonDescription"
                                    defaultMessage="Additional comments"
                                />
                            }
                            variant="outlined"
                        />
                    )}
                />
            </Box>
        </Box>
    );
};

export default AccountStatusFormReason;
